import { useState, useCallback, useRef, useEffect } from "react";
import type { AccountTreeGroup } from "../../../../shared/domain/entities/AccountTreeEntity";
import { mergeTreeGroups } from "../../../../shared/utils/glTreeUtils";
import { fetchGlParentGroupTree } from "../../data/api/glParentGroupTreeApi";
import type { GlTreePickerHookState } from "../../../../shared/presentation/types/glTreePickerTypes";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export interface UseGlParentGroupTreeOptions {
  accountType: string;
  selectedAccountId?: string;
  enabled?: boolean;
}

export function useGlParentGroupTree({
  accountType,
  selectedAccountId,
  enabled = true,
}: UseGlParentGroupTreeOptions): GlTreePickerHookState {
  const [groups, setGroups] = useState<AccountTreeGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingTypes] = useState<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTermState] = useState("");
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef(0);
  const debouncedSearchRef = useRef("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingMoreRef = useRef(false);

  const searchActive = searchTerm.trim().length > 0;

  const fetchBrowseTree = useCallback(async (): Promise<void> => {
    if (!enabled || !accountType) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchGlParentGroupTree({
        accountType,
        selectedAccountId,
      });

      if (!result.success) {
        throw new Error("Failed to fetch parent groups");
      }

      setGroups(result.data.groups ?? []);
      setHasMore(false);
      offsetRef.current = 0;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load parent groups";
      setError(message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [accountType, enabled, selectedAccountId]);

  const fetchSearchPage = useCallback(
    async (search: string, offset: number, append: boolean): Promise<void> => {
      if (!enabled || !accountType) return;

      if (append) {
        setLoadingMore(true);
        isLoadingMoreRef.current = true;
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await fetchGlParentGroupTree({
          accountType,
          search,
          limit: PAGE_SIZE,
          offset,
        });

        if (!result.success) {
          throw new Error("Failed to fetch parent groups");
        }

        setGroups((prev) =>
          append
            ? mergeTreeGroups(prev, result.data.groups ?? [])
            : (result.data.groups ?? []),
        );

        const nextHasMore = result.data.pagination?.has_more ?? false;
        setHasMore(nextHasMore);
        offsetRef.current = offset;
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to load parent groups";
        setError(message);
        if (!append) {
          setGroups([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
        isLoadingMoreRef.current = false;
      }
    },
    [accountType, enabled],
  );

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
  }, []);

  const loadAccountType = useCallback(async (_accountType: string): Promise<void> => {
    // Browse tree is loaded eagerly for the selected account type.
  }, []);

  const loadMore = useCallback(() => {
    if (
      !searchActive ||
      !hasMore ||
      loading ||
      loadingMore ||
      isLoadingMoreRef.current
    ) {
      return;
    }
    const nextOffset = offsetRef.current + PAGE_SIZE;
    fetchSearchPage(debouncedSearchRef.current, nextOffset, true);
  }, [fetchSearchPage, hasMore, loading, loadingMore, searchActive]);

  useEffect(() => {
    if (!enabled || !accountType) {
      setGroups([]);
      setHasMore(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const trimmed = searchTerm.trim();
      debouncedSearchRef.current = trimmed;

      if (trimmed) {
        fetchSearchPage(trimmed, 0, false);
      } else {
        fetchBrowseTree();
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [accountType, enabled, fetchBrowseTree, fetchSearchPage, searchTerm]);

  return {
    groups,
    loading,
    loadingMore,
    loadingTypes,
    hasMore,
    searchTerm,
    searchActive,
    selectedAccountType: accountType || null,
    setSearchTerm,
    loadAccountType,
    loadMore,
    error,
  };
}
